import styled from 'styled-components';
import { useRecoilValue } from 'recoil';
import { blockSettings, hideBlockBorders, isSelectedBlockState } from 'venn-state';
import { SpecialCssClasses, useHasFF } from 'venn-utils';
import React, { forwardRef } from 'react';
import { cva } from 'class-variance-authority';
import { cn } from '@/lib/utils';
import { ACTIVE_BLOCK_CLASS } from '../../utils/constants';
import { GetColor } from 'venn-ui-kit';

const outerBorderVariants = cva('group border-solid w-full print:break-inside-avoid', {
  variants: {
    active: {
      true: 'border-2',
      false: 'cursor-pointer hover:border-2 hover:border-venn-grey',
    },
    transparent: {
      true: 'bg-transparent',
      false: 'bg-white',
    },
    hasBlockAssignment: {
      true: '',
      false: '',
    },
  },
  compoundVariants: [
    {
      active: false,
      transparent: false,
      className: 'border border-venn-grey',
    },
    {
      active: false,
      transparent: true,
      className: 'border-2 border-transparent',
    },
    {
      active: true,
      hasBlockAssignment: true,
      className: 'border-venn-dark-blue',
    },
    {
      active: true,
      hasBlockAssignment: false,
      className: 'border-venn-primary-main',
    },
  ],
  defaultVariants: {
    active: false,
    transparent: false,
  },
});

const innerBorderVariants = cva('border-solid border-transparent w-full h-full flex flex-col', {
  variants: {
    active: {
      true: 'border-0',
      false: 'group-hover:border-0',
    },
    transparent: {
      true: 'border-0',
      false: 'border',
    },
  },
  defaultVariants: {
    active: false,
    transparent: false,
  },
});

interface BorderContainerProps {
  id: string;
  children: React.ReactNode;
  'data-testid'?: string;
}

export const BorderContainer = forwardRef<HTMLDivElement, BorderContainerProps>(
  function InternalBorderContainer(props, ref) {
    const { id, children } = props;

    const active = useRecoilValue(isSelectedBlockState(id));
    const settings = useRecoilValue(blockSettings(id));
    const hideBorder = useRecoilValue(hideBlockBorders(id));
    const transparent = settings.customBlockType === 'PAGE_BREAK' || hideBorder;
    const hasBlockAssignment = useHasFF('block_assignment_ff');

    return (
      <StyledBorder
        ref={ref}
        data-testid={props['data-testid']}
        className={cn(outerBorderVariants({ active, transparent, hasBlockAssignment }), active && ACTIVE_BLOCK_CLASS)}
      >
        <div className={innerBorderVariants({ active, transparent })}>{children}</div>
      </StyledBorder>
    );
  },
);

const StyledBorder = styled.div`
  .${SpecialCssClasses.ExportAsImage} & {
    border-color: ${GetColor.Grey};
  }
`;
